// ---------------------------------------------------------------------------
// Intake-decision grammar — the single definition of the four decision blocks
// Studio's intake cards (WonBanking scenarios S1-S4/S7 lane, 2026-08-08)
// append under a generation prompt:
//
//   [field-visibility-decision]     S2 — which spec fields the screen shows
//   [legacy-conversion-decision]    S3 — carry-vs-improve rulings
//   [screen-design-decision]        S4 — verbatim labels + explicit
//                                        screen-first (no data source)
//   [reference-extraction-decision] S7 — the standard extracted from a
//                                        reference, exclusions written out
//
// The blocks are human-confirmed rulings, serialized by the studio's intake
// cards. This module is the read side: the studio client parses them to send
// a STRUCTURED `intakeDecisions` payload alongside the prompt, and
// cogo-decompose-api validates that payload, strips the raw blocks out of
// the text it plans over, and turns the data into explicit planner
// constraints. One grammar, one package both sides already depend on — the
// alternative (a client copy and a server copy) is the two-surfaces-drift
// failure this project keeps re-finding.
//
// Everything here is pure and deterministic. Known, accepted limit: block
// values are comma-joined, so a term containing ", " does not survive the
// round trip; the studio's own suite pins formatter↔parser compatibility.
// ---------------------------------------------------------------------------

export type FieldVisibilitySection = "input" | "output" | "common" | "unknown";

export interface FieldVisibilityDecision {
  /** Fields the human kept on screen, by spec section. */
  visible: Partial<Record<FieldVisibilitySection, string[]>>;
  /** Fields the human ruled internal — declared, transported, never shown. */
  internal: string[];
}

export interface LegacyConversionDecision {
  /** Trait keys carried as-is (faithful conversion is the baseline). */
  keep: string[];
  /** Trait keys the human explicitly ruled improved. */
  improve: string[];
}

export interface ScreenDesignDecision {
  /** Labels the design document quoted — used verbatim, never re-invented. */
  labelsVerbatim: string[];
  /**
   * What the S4 read found about where the data comes from:
   *   - `undeclared-screen-first` — the document declares none, the human was
   *     told, and chose to proceed with the screen only;
   *   - `declared-in-document`  — the document names its own endpoints /
   *     transactions / field codes, listed in `dataSourceReferences`.
   *
   * Before the second value existed the first was written unconditionally, so
   * a 설계서 declaring `GET /api/loan/limit/list` was recorded as having
   * declared nothing, and `buildIntakeDecisionPromptSection` turned that into
   * "NO invented API endpoints or field bindings" — discarding the document's
   * own bindings in the name of a human confirmation that never happened.
   */
  dataSource: "undeclared-screen-first" | "declared-in-document";
  /**
   * The machine-readable data references the document declared, verbatim.
   * Empty for a screen-first decision.
   */
  dataSourceReferences: string[];
  /**
   * Code-shaped tokens the S4 read SAW and could not confirm as data
   * references — nothing in the document said what they are bound to.
   *
   * They are not references and must never be bound to. They exist here for
   * two claims that would otherwise be false: beside a screen-first decision,
   * "the user confirmed no data source is declared" (they can only confirm an
   * absence they were shown); and beside a declared one, "use ONLY these
   * references and invent no others", which asserts a completeness the read
   * cannot always have.
   */
  dataSourceUncounted: string[];
}

export interface ReferenceExtractionDecision {
  structure: "carried" | "not-carried";
  /** Terms the human promoted to standard vocabulary. */
  standardTerms: string[];
  /** Terms ruled this-screen-specific — excluded by decision, not oversight. */
  excludedScreenSpecific: string[];
}

export interface IntakeDecisions {
  fieldVisibility?: FieldVisibilityDecision;
  legacyConversion?: LegacyConversionDecision;
  screenDesign?: ScreenDesignDecision;
  referenceExtraction?: ReferenceExtractionDecision;
}

const BLOCK_HEADERS = [
  "field-visibility-decision",
  "legacy-conversion-decision",
  "screen-design-decision",
  "reference-extraction-decision",
] as const;

type BlockHeader = (typeof BLOCK_HEADERS)[number];

const SCREEN_FIRST_MARKER = "undeclared — proceed screen-first";
const DATA_DECLARED_MARKER = "declared — read from the document";

interface RawBlock {
  header: BlockHeader;
  entries: Array<{ key: string; value: string }>;
  /** [start, end) line span in the source, including the header line. */
  span: [number, number];
}

function splitList(value: string): string[] {
  return value
    .split(", ")
    .map((entry) => entry.trim())
    .filter(Boolean);
}

/** A block's body line: `key: value` (key = lowercase kebab, optional (…)). */
const ENTRY_PATTERN = /^([a-z-]+(?:\([a-z]+\))?):\s*(.+)$/;

function scanBlocks(text: string): { blocks: RawBlock[]; lines: string[] } {
  const lines = text.split(/\r?\n/);
  const blocks: RawBlock[] = [];
  let index = 0;
  while (index < lines.length) {
    const headerMatch = /^\[([a-z-]+)\]\s*$/.exec(lines[index].trim());
    const header = headerMatch?.[1];
    if (!header || !(BLOCK_HEADERS as readonly string[]).includes(header)) {
      index += 1;
      continue;
    }
    const entries: RawBlock["entries"] = [];
    let cursor = index + 1;
    while (cursor < lines.length) {
      const entry = ENTRY_PATTERN.exec(lines[cursor].trim());
      if (!entry) break;
      entries.push({ key: entry[1], value: entry[2].trim() });
      cursor += 1;
    }
    blocks.push({
      header: header as BlockHeader,
      entries,
      span: [index, cursor],
    });
    index = cursor;
  }
  return { blocks, lines };
}

function readBlock(block: RawBlock, into: IntakeDecisions): void {
  switch (block.header) {
    case "field-visibility-decision": {
      const decision: FieldVisibilityDecision = { visible: {}, internal: [] };
      for (const { key, value } of block.entries) {
        const section = /^visible\((input|output|common|unknown)\)$/.exec(key);
        if (section) {
          decision.visible[section[1] as FieldVisibilitySection] =
            splitList(value);
        } else if (key === "internal") {
          decision.internal = splitList(value);
        }
      }
      into.fieldVisibility = decision;
      return;
    }
    case "legacy-conversion-decision": {
      const decision: LegacyConversionDecision = { keep: [], improve: [] };
      for (const { key, value } of block.entries) {
        if (key === "keep") decision.keep = splitList(value);
        else if (key === "improve") decision.improve = splitList(value);
      }
      into.legacyConversion = decision;
      return;
    }
    case "screen-design-decision": {
      const decision: ScreenDesignDecision = {
        labelsVerbatim: [],
        dataSource: "undeclared-screen-first",
        dataSourceReferences: [],
        dataSourceUncounted: [],
      };
      for (const { key, value } of block.entries) {
        if (key === "labels-verbatim") {
          decision.labelsVerbatim = splitList(value);
        } else if (key === "data-source-declared") {
          decision.dataSourceReferences = splitList(value);
        } else if (key === "data-source-uncounted") {
          decision.dataSourceUncounted = splitList(value);
        } else if (key === "data-source") {
          // Two legal markers, both written by the formatter. Anything else
          // is grammar drift and the block is refused rather than guessed.
          if (value === DATA_DECLARED_MARKER) {
            decision.dataSource = "declared-in-document";
          } else if (value !== SCREEN_FIRST_MARKER) {
            return;
          }
        }
      }
      into.screenDesign = decision;
      return;
    }
    case "reference-extraction-decision": {
      const decision: ReferenceExtractionDecision = {
        structure: "carried",
        standardTerms: [],
        excludedScreenSpecific: [],
      };
      for (const { key, value } of block.entries) {
        if (key === "structure") {
          if (value !== "carried" && value !== "not-carried") return;
          decision.structure = value;
        } else if (key === "standard-terms") {
          decision.standardTerms = splitList(value);
        } else if (key === "excluded-screen-specific") {
          decision.excludedScreenSpecific = splitList(value);
        }
      }
      into.referenceExtraction = decision;
      return;
    }
  }
}

/** Reads every decision block out of a prompt; null when none are present. */
export function parseIntakeDecisions(text: string): IntakeDecisions | null {
  const { blocks } = scanBlocks(text);
  if (blocks.length === 0) return null;
  const decisions: IntakeDecisions = {};
  for (const block of blocks) readBlock(block, decisions);
  return Object.keys(decisions).length > 0 ? decisions : null;
}

/**
 * Returns the text with every decision block removed — what a planner should
 * read as the document/request itself. Blank runs left behind by a removed
 * block collapse to one separator; as a side effect, a 3+-newline run that
 * was already in the document collapses too (planning text, not a byte-exact
 * archive — the original prompt is preserved elsewhere).
 */
export function stripIntakeDecisionBlocks(text: string): string {
  const { blocks, lines } = scanBlocks(text);
  if (blocks.length === 0) return text;
  const drop = new Set<number>();
  for (const block of blocks) {
    for (let line = block.span[0]; line < block.span[1]; line += 1) {
      drop.add(line);
    }
  }
  const kept = lines.filter((_, index) => !drop.has(index));
  return kept.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

function stringArray(value: unknown): string[] | null {
  return Array.isArray(value) &&
    value.every((entry) => typeof entry === "string")
    ? (value as string[])
    : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Structural validation for an `intakeDecisions` payload arriving over the
 * wire. Returns a clean copy or null — a caller that gets null should fail
 * closed (reject the request) rather than guess, so grammar drift between
 * client and server versions is visible instead of silently dropped.
 */
export function validateIntakeDecisions(value: unknown): IntakeDecisions | null {
  if (!isRecord(value)) return null;
  const out: IntakeDecisions = {};
  const knownKeys = [
    "fieldVisibility",
    "legacyConversion",
    "screenDesign",
    "referenceExtraction",
  ];
  for (const key of Object.keys(value)) {
    if (!knownKeys.includes(key)) return null;
  }

  if (value.fieldVisibility !== undefined) {
    if (!isRecord(value.fieldVisibility)) return null;
    const visible = value.fieldVisibility.visible;
    const internal = stringArray(value.fieldVisibility.internal ?? []);
    if (!isRecord(visible) || !internal) return null;
    const sections: FieldVisibilityDecision["visible"] = {};
    for (const [section, names] of Object.entries(visible)) {
      if (!["input", "output", "common", "unknown"].includes(section)) {
        return null;
      }
      const list = stringArray(names);
      if (!list) return null;
      sections[section as FieldVisibilitySection] = list;
    }
    out.fieldVisibility = { visible: sections, internal };
  }

  if (value.legacyConversion !== undefined) {
    if (!isRecord(value.legacyConversion)) return null;
    const keep = stringArray(value.legacyConversion.keep ?? []);
    const improve = stringArray(value.legacyConversion.improve ?? []);
    if (!keep || !improve) return null;
    out.legacyConversion = { keep, improve };
  }

  if (value.screenDesign !== undefined) {
    if (!isRecord(value.screenDesign)) return null;
    const labels = stringArray(value.screenDesign.labelsVerbatim ?? []);
    if (!labels) return null;
    const dataSource = value.screenDesign.dataSource;
    if (
      dataSource !== "undeclared-screen-first" &&
      dataSource !== "declared-in-document"
    ) {
      return null;
    }
    const references = stringArray(
      value.screenDesign.dataSourceReferences ?? [],
    );
    const uncounted = stringArray(
      value.screenDesign.dataSourceUncounted ?? [],
    );
    if (!references || !uncounted) return null;
    out.screenDesign = {
      labelsVerbatim: labels,
      dataSource,
      dataSourceReferences: references,
      dataSourceUncounted: uncounted,
    };
  }

  if (value.referenceExtraction !== undefined) {
    if (!isRecord(value.referenceExtraction)) return null;
    const structure = value.referenceExtraction.structure;
    if (structure !== "carried" && structure !== "not-carried") return null;
    const standardTerms = stringArray(
      value.referenceExtraction.standardTerms ?? [],
    );
    const excluded = stringArray(
      value.referenceExtraction.excludedScreenSpecific ?? [],
    );
    if (!standardTerms || !excluded) return null;
    out.referenceExtraction = {
      structure,
      standardTerms,
      excludedScreenSpecific: excluded,
    };
  }

  return Object.keys(out).length > 0 ? out : null;
}

/**
 * Renders the decisions as an explicit planner-constraint section, appended
 * after the decomposition prompt. Framed as human-confirmed and
 * non-negotiable — these are choices a person already made on the intake
 * card, and the planner's job is to honour them, not to re-decide them. The
 * raw `[…]` block syntax never appears here; the model sees constraints,
 * not a serialization format to imitate.
 */
/** `input.custNo` — the studio's section-qualified form, not a bare name. */
function isSectionQualified(name: string): boolean {
  return /^(input|output|common|unknown)\./.test(name);
}

export function buildIntakeDecisionPromptSection(
  decisions: IntakeDecisions,
): string {
  const lines: string[] = [
    "HUMAN-CONFIRMED INTAKE DECISIONS (non-negotiable — the user already ruled on these; do not re-decide them):",
  ];

  const fv = decisions.fieldVisibility;
  if (fv) {
    const visible = (
      ["input", "output", "common", "unknown"] as const
    ).flatMap((section) => fv.visible[section] ?? []);
    if (visible.length > 0) {
      lines.push(
        `- Show EXACTLY these spec fields on screen: ${visible.join(", ")}.`,
      );
    }
    if (fv.internal.length > 0) {
      lines.push(
        `- These fields are internal-only — never render them: ${fv.internal.join(", ")}.`,
      );
    }
    // A name declared in two sections of one spec is written `input.custNo` /
    // `output.custNo` so the human's ruling cannot flatten into "show custNo"
    // beside "never render custNo". Unexplained, that dot reaches the model
    // as something it must interpret, and `input.`/`output.` are not among
    // the canonical expression prefixes — so the likeliest reading is the
    // wrong one. Emitted only when a qualified name is actually present.
    if ([...visible, ...fv.internal].some(isSectionQualified)) {
      lines.push(
        "- A field written `section.name` (e.g. `input.custNo`) is one the spec declares in more than one place: the part before the dot is the section of the message spec it belongs to, and the field's own name is the part after it. It is NOT an expression prefix — do not emit `input.`/`output.` into bindings.",
      );
    }
  }

  const lc = decisions.legacyConversion;
  if (lc) {
    if (lc.keep.length > 0) {
      lines.push(
        `- Legacy conversion, carry AS-IS (do not modernize): ${lc.keep.join(", ")}.`,
      );
    }
    if (lc.improve.length > 0) {
      lines.push(
        `- Legacy conversion, IMPROVE with modern equivalents: ${lc.improve.join(", ")}.`,
      );
    }
  }

  const sd = decisions.screenDesign;
  if (sd) {
    if (sd.labelsVerbatim.length > 0) {
      lines.push(
        `- Use these labels VERBATIM as written (the design document quoted them): ${sd.labelsVerbatim.join(", ")}.`,
      );
    }
    const uncounted = sd.dataSourceUncounted;
    if (sd.dataSource === "declared-in-document") {
      // The document named its own data. Telling the planner to invent
      // nothing here would discard exactly what the document declared.
      //
      // "use ONLY these" is a claim that the list is COMPLETE. It is made only
      // where the read has no uncounted candidates — otherwise it would turn a
      // partial read into a closed world, which is how a truncated list became
      // the document's whole data source.
      if (sd.dataSourceReferences.length === 0) {
        lines.push(
          "- The design document declares its own data source: bind to what it names and invent no endpoints of your own.",
        );
      } else if (uncounted.length === 0) {
        lines.push(
          `- The design document DECLARES its data source: use ONLY these references and invent no others: ${sd.dataSourceReferences.join(", ")}.`,
        );
      } else {
        lines.push(
          `- The design document DECLARES its data source; bind to these references: ${sd.dataSourceReferences.join(", ")}. The read also saw ${uncounted.length} code-shaped token(s) it could not confirm as data references (${uncounted.join(", ")}), so this list is what was READ and may not be the document's complete set — invent no bindings of your own, and treat nothing outside it as ruled out.`,
        );
      }
    } else if (uncounted.length > 0) {
      // The human ruled screen-first over a document that visibly holds
      // code-shaped tokens. The ruling stands and the constraint is unchanged;
      // what must not be claimed is that they confirmed an absence — they were
      // shown these and chose to proceed anyway.
      lines.push(
        `- The user was shown ${uncounted.length} code-shaped token(s) this document mentions without saying what they bind to (${uncounted.join(", ")}), and chose to proceed screen-first: build the screen first, with placeholder-free static structure and NO invented API endpoints or field bindings. Do NOT bind to those tokens — they are unconfirmed, not a data source.`,
      );
    } else {
      lines.push(
        "- The user confirmed no data source is declared: build the screen first, with placeholder-free static structure and NO invented API endpoints or field bindings.",
      );
    }
  }

  const re = decisions.referenceExtraction;
  if (re) {
    lines.push(
      re.structure === "carried"
        ? "- Reference extraction: carry the reference's STRUCTURE pattern (area composition), not its content."
        : "- Reference extraction: do NOT carry the reference's structure.",
    );
    if (re.standardTerms.length > 0) {
      lines.push(
        `- Reference terms ruled standard vocabulary: ${re.standardTerms.join(", ")}.`,
      );
    }
    if (re.excludedScreenSpecific.length > 0) {
      lines.push(
        `- Reference terms ruled screen-specific — EXCLUDED, do not reuse them: ${re.excludedScreenSpecific.join(", ")}.`,
      );
    }
  }

  return lines.join("\n");
}
